// SB6-ANA-FILT-06 (Track B) — Defect Filter Widget.
//
// Advanced defect-side filtering, opened from the L3 Kind "Defect"
// button's sliders icon (D4) or from Settings → Layout & Widgets. Built
// on the generic `FacetTableWidget` primitive (S1):
//
//   ☑ | defect type name | # defects | severity distribution
//
//   * Rows come from `GET …/{id}/defect-type-facets` (L1+L2-scoped — we
//     pass the global filter's status + reviewState).
//   * A "Show all Defect Types" toggle refetches with includeEmpty.
//   * The severity column (a 100%-stacked bar coloured by each segment's
//     colorCode) is HIDDEN entirely when `hasClassModel` is false (D10).
//   * Checking/unchecking types drives the global `defectTypeIds` filter
//     axis so the data-point set narrows.
//   * The "Filter active" header switch is two-way synced with the L3
//     Defect option button (D4) using the most-recent-action-wins rule
//     (S8): the widget reflects whether "defect" is in the kind axis, and
//     flipping the switch adds/removes "defect" from the kind axis.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Tooltip,
} from "@mui/material";
import type { DefectTypeFacet, SeveritySegment } from "@aeriosiq/types";
import {
  useDataAcquisitionDetailQuery,
  useDefectTypeFacetsQuery,
} from "../../queries.js";
import { useDataPointFilter } from "../../useDataPointFilter.js";
import {
  FacetTableWidget,
  type FacetExtraColumn,
  type FacetRow,
} from "./FacetTableWidget.js";
import { FilterConfigPresets } from "./FilterConfigPresets.js";
import { useWidgetFilterConfigs } from "../../useWidgetFilterConfigs.js";
import {
  buildDefectConfigBlob,
  deriveDefectModelSignature,
  gateDefectConfigBlob,
  parseDefectConfigBlob,
} from "../../filterConfigBlob.js";

interface DefectFilterWidgetProps {
  dataAcquisitionId: string;
}

const DEFECT_KIND = "defect";

/** Pick a localized label, preferring `en`, then `de`, then any value. */
function localized(name: Record<string, string>): string {
  return name.en ?? name.de ?? Object.values(name)[0] ?? "";
}

interface DefectRow extends FacetRow {
  severityDistribution: SeveritySegment[] | null;
}

/**
 * Horizontal 100%-stacked severity bar (S4 / D10). Each segment is a
 * grade coloured by its `colorCode`, widths proportional to its share of
 * the type's total; a per-segment tooltip reads "grade N: k".
 */
function SeverityBar({
  segments,
}: {
  segments: SeveritySegment[];
}): React.ReactElement {
  const total = segments.reduce((acc, s) => acc + s.count, 0);
  if (total === 0) {
    return (
      <Box
        data-testid="severity-bar-empty"
        sx={{ height: 12, width: "100%", opacity: 0.3 }}
      />
    );
  }
  return (
    <Box
      data-testid="severity-bar"
      role="img"
      aria-label={segments
        .map((s) => `grade ${String(s.grade)}: ${String(s.count)}`)
        .join(", ")}
      sx={{
        display: "flex",
        height: 12,
        width: "100%",
        minWidth: 80,
        borderRadius: 0.5,
        overflow: "hidden",
      }}
    >
      {segments.map((s) => (
        <Tooltip
          key={s.grade}
          title={`grade ${String(s.grade)}: ${String(s.count)}`}
        >
          <Box
            data-testid={`severity-segment-${String(s.grade)}`}
            sx={{
              flexGrow: s.count,
              flexBasis: 0,
              backgroundColor: s.colorCode,
            }}
          />
        </Tooltip>
      ))}
    </Box>
  );
}

export function DefectFilterWidget({
  dataAcquisitionId,
}: DefectFilterWidgetProps): React.ReactElement {
  const { selection, setAxis } = useDataPointFilter();
  const [showAll, setShowAll] = useState(false);

  const defectFacetsQuery = useDefectTypeFacetsQuery(
    dataAcquisitionId,
    selection,
    { includeEmpty: showAll },
  );
  const { data, isPending, isError } = defectFacetsQuery;

  // SB6-ANA-PROG-03 — per-widget refetch indication removed (the bottom
  // status overlay + the FilterWidget aggregate cover it). The widget keeps
  // its true first-load spinner (`isPending`); a refetch for a newer
  // revision keeps the previous rows full-contrast (keepPreviousData).

  const hasClassModel = data?.hasClassModel ?? false;
  const facetItems: DefectTypeFacet[] = useMemo(
    () => data?.items ?? [],
    [data],
  );

  const rows: DefectRow[] = useMemo(
    () =>
      facetItems.map((it) => ({
        id: it.catalogueEntryId,
        name: localized(it.name),
        count: it.defectCount,
        severityDistribution: it.severityDistribution,
      })),
    [facetItems],
  );

  // The global `defectTypeIds` selection is the source of truth for the
  // checked rows; the widget mirrors it (and writes back through setAxis).
  const checkedIds = useMemo(
    () => new Set(selection.defectType),
    [selection.defectType],
  );

  const toggleRow = useCallback(
    (id: string, checked: boolean) => {
      const next = checked
        ? [...selection.defectType, id]
        : selection.defectType.filter((v) => v !== id);
      setAxis("defectType", next);
    },
    [selection.defectType, setAxis],
  );

  const toggleAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        // Add every VISIBLE row id (union with any already-checked ids
        // that may not be in the current visible set).
        const union = new Set(selection.defectType);
        for (const r of rows) union.add(r.id);
        setAxis("defectType", Array.from(union));
      } else {
        // Remove only the visible row ids.
        const visible = new Set(rows.map((r) => r.id));
        setAxis(
          "defectType",
          selection.defectType.filter((v) => !visible.has(v)),
        );
      }
    },
    [rows, selection.defectType, setAxis],
  );

  // --- "Filter active" ⇄ L3 Defect option two-way sync (D4 / S8) -------
  // The widget switch reflects whether "defect" is in the global kind
  // axis. Flipping the switch adds/removes "defect" from the kind axis.
  // Most-recent-action-wins: a `setAxis` write IS the authoritative
  // action, and the kind selection is the single shared state, so there
  // is no separate local "active" flag to drift — the switch is derived.
  const active = selection.kind.includes(DEFECT_KIND);
  // Guard so a programmatic prop change never loops back into a write.
  const lastWrittenRef = useRef<boolean | null>(null);
  useEffect(() => {
    lastWrittenRef.current = active;
  }, [active]);

  const setActive = useCallback(
    (nextActive: boolean) => {
      if (nextActive === active) return;
      const next = nextActive
        ? [...selection.kind, DEFECT_KIND]
        : selection.kind.filter((k) => k !== DEFECT_KIND);
      lastWrittenRef.current = nextActive;
      setAxis("kind", next);
    },
    [active, selection.kind, setAxis],
  );

  // --- Save/Load presets + last-used persistence (Track D / D9) --------
  // The defect side has no single BE `modelSignature` field, so derive a
  // deterministic one from the facets' `classModelId` (which severity
  // grades exist) + the DA's active catalogue (which defect types are
  // checkable). A preset loads only when BOTH match (D9).
  const { data: detail } = useDataAcquisitionDetailQuery(dataAcquisitionId);
  const modelSignature = useMemo(
    () =>
      data
        ? deriveDefectModelSignature({
            classModelId: data.classModelId,
            catalogueId: detail?.activeCatalogueId ?? null,
          })
        : "",
    [data, detail?.activeCatalogueId],
  );
  // Defect types that currently HAVE data (validity-gating universe — D9).
  const presentTypeIds = useMemo(() => {
    const s = new Set<string>();
    for (const it of facetItems) {
      if (it.defectCount > 0) s.add(it.catalogueEntryId);
    }
    return s;
  }, [facetItems]);

  // The live config blob: checked types (the global defectTypeIds axis) +
  // the active toggle (the L3 Defect kind selection).
  const configBlob = useMemo(
    () =>
      buildDefectConfigBlob(active, selection.defectType) as unknown as Record<
        string,
        unknown
      >,
    [active, selection.defectType],
  );

  // Apply a stored blob, validity-gated: drop checked types that no longer
  // have data, write them into the global `defectTypeIds`, and sync the
  // active toggle (L3 Defect kind) to the blob's `active`.
  const applyBlob = useCallback(
    (raw: Record<string, unknown>) => {
      const blob = parseDefectConfigBlob(raw);
      setAxis("defectType", gateDefectConfigBlob(blob, presentTypeIds));
      const next = blob.active
        ? selection.kind.includes(DEFECT_KIND)
          ? selection.kind
          : [...selection.kind, DEFECT_KIND]
        : selection.kind.filter((k) => k !== DEFECT_KIND);
      lastWrittenRef.current = blob.active;
      setAxis("kind", next);
    },
    [presentTypeIds, selection.kind, setAxis],
  );

  const presetsApi = useWidgetFilterConfigs({
    scope: { kind: "defect", modelSignature },
    blob: configBlob,
    facetsReady: !isPending && data !== undefined,
    applyBlob,
  });

  const extraColumn: FacetExtraColumn<DefectRow> | null = hasClassModel
    ? {
        header: "Severity distribution",
        width: 160,
        render: (row) =>
          row.severityDistribution ? (
            <SeverityBar segments={row.severityDistribution} />
          ) : null,
      }
    : null;

  const toolbar = (
    <FormControlLabel
      control={
        <Switch
          size="small"
          checked={showAll}
          onChange={(e) => {
            setShowAll(e.target.checked);
          }}
          data-testid="defect-filter-show-all"
          inputProps={{ "aria-label": "Show all Defect Types" }}
        />
      }
      label="Show all Defect Types"
      sx={{ m: 0 }}
    />
  );

  if (isError) {
    return (
      <Box
        data-testid="defect-filter-widget"
        role="region"
        aria-label="Defect Filter Widget"
        sx={{ p: 2 }}
      >
        <Alert severity="error">Could not load defect types.</Alert>
      </Box>
    );
  }

  return (
    <FacetTableWidget<DefectRow>
      ariaLabel="Defect Filter Widget"
      testId="defect-filter-widget"
      rows={rows}
      nameHeader="Defect type"
      countHeader="# defects"
      extraColumn={extraColumn}
      checkedIds={checkedIds}
      onToggleRow={toggleRow}
      onToggleAll={toggleAll}
      active={active}
      onActiveChange={setActive}
      toolbar={toolbar}
      headerSlot={
        <FilterConfigPresets
          testId="defect-filter-presets"
          presets={presetsApi.presets}
          selectedId={presetsApi.selectedId}
          disabled={presetsApi.disabled}
          busy={presetsApi.busy}
          onSelect={presetsApi.onSelect}
          onSaveAs={presetsApi.onSaveAs}
          onUpdate={presetsApi.onUpdate}
          onDelete={presetsApi.onDelete}
        />
      }
      emptyMessage={
        isPending ? (
          <Stack alignItems="center" spacing={1}>
            <CircularProgress size={20} />
          </Stack>
        ) : showAll ? (
          "No defect catalogue types."
        ) : (
          "No defect types present. Enable “Show all Defect Types”."
        )
      }
    />
  );
}
